import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UploadDialogComponent } from 'src/app/admin/manage-personas/edit/upload-dialog/upload-dialog';
import { FileDropperDirective } from '../../utils/file-dropper.directive';
import { catchError, finalize, firstValueFrom, Subscription, switchMap, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { LoaderButtonComponent } from '../loader-button/loader-button.component';
import { WorkspaceTemplateService } from '@workspaces/services/templates.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { TemplateFetch } from '@workspaces/store/template.feature';
import { AppState } from 'src/app/state/app.state';
import { isFileSupported, SUPPORTED_EXTENSIONS } from '@workspaces/utils/supported-extensions';

export interface UploadFile {
  file: File;
  name: string;
}
@Component({
  selector: 'app-template-file-upload',
  templateUrl: './template-file-upload.component.html',
  styleUrls: ['./template-file-upload.component.scss'],
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule, CommonModule, FileDropperDirective, LoaderButtonComponent]
})
export class TemplateFileUploadComponent implements OnDestroy {
  public file: UploadFile | null = null;
  public isSubmitting = false;
  public readonly supportedFileExtensions = SUPPORTED_EXTENSIONS;
  private readonly sub = new Subscription();
  constructor(
    public readonly dialogRef: MatDialogRef<UploadDialogComponent>,
    public readonly templateService: WorkspaceTemplateService,
    @Inject(MAT_DIALOG_DATA) private readonly templateData: { templateId: string; version: string },
    private readonly http: HttpClient,
    private readonly snackBar: MatSnackBar,
    private readonly store: Store<AppState>
  ) {}

  handleFiles(fileList: FileList | null): void {
    if (!fileList) {
      return;
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);

      if (file && isFileSupported(file.name)) {
        if (this.file?.name !== file.name) {
          this.file = { file: file, name: file.name };
        }
      }
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.handleFiles(input.files);
    input.value = '';
  }

  onFileDropped(fileList: FileList): void {
    this.handleFiles(fileList);
  }

  removeFile(): void {
    this.file = null;
  }

  onSave(): void {
    this.isSubmitting = true;
    if (!this.file) {
      return;
    }
    const extension = this.file.name.split('.').pop() ?? '';
    const uploadProcess$ = this.templateService
      .generateUploadUrl(this.templateData.templateId, this.templateData.version, extension)
      .pipe(
        switchMap(resp => this.uploadToS3({ url: resp.url, file: this.file!.file })),
        catchError((error: HttpErrorResponse) => {
          this.snackBar.open('Failed to upload file.', 'Close', {
            duration: 3000,
            panelClass: ['error-snackbar']
          });
          return throwError(() => error);
        }),
        finalize(() => {
          this.isSubmitting = false;
          this.store.dispatch(TemplateFetch({ show_latest: false }));
        })
      );

    this.sub.add(
      uploadProcess$.subscribe({
        next: () => {
          this.snackBar.open('File uploaded successfully', 'Success', { duration: 2000 });
          this.dialogRef.close(true);
        }
      })
    );
  }

  uploadToS3(uploadFileWithUrl: { file: File; url: string }) {
    return firstValueFrom(
      this.http.put(uploadFileWithUrl.url, uploadFileWithUrl.file, {
        headers: new HttpHeaders({
          'Content-Type': uploadFileWithUrl.file.type
        })
      })
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
